import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Database } from '@/integrations/supabase/types';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

export type ProjectRow = Database['public']['Tables']['projects']['Row'];
export type MediaRow = Database['public']['Tables']['media']['Row'];
export type ProjectFileRow = Database['public']['Tables']['project_files']['Row'];

export interface MediaProjectAssignment {
  mediaId: string;
  projectId: string;
}

export interface AddMediaToProjectsInput {
  mediaIds: string[];
  projectIds: string[];
}

export interface AddMediaToProjectsResult {
  inserted: number;
  projectNames: string[];
}

export interface ProjectMediaItem {
  media: MediaRow;
  favoritedAt: string | null;
  favoritedBy: string | null;
  assignedAt: string | null;
}

type ProjectMediaAssignmentRow = {
  id: string;
  artifact_id: string | null;
  project_task_id: string | null;
  created_at: string;
  favorited_at: string | null;
  favorited_by: string | null;
};

/**
 * Project Library is the aggregate of direct (unbound) Project assignments.
 * Concept links are contextual fulfillment edges, so they neither make a
 * library item appear assigned nor participate in its favorite state. The
 * database uniqueness constraint normally gives one direct row per media;
 * sorting by timestamp and id still makes legacy duplicate reads deterministic.
 */
export function aggregateDirectProjectMediaAssignments(
  files: readonly ProjectMediaAssignmentRow[],
): Map<string, Omit<ProjectMediaItem, 'media'>> {
  const assignments = new Map<string, Omit<ProjectMediaItem, 'media'>>();
  const directRows = files
    .filter((file) => file.project_task_id === null && Boolean(file.artifact_id))
    .sort((a, b) => {
      const timestampOrder = b.created_at.localeCompare(a.created_at);
      return timestampOrder !== 0 ? timestampOrder : b.id.localeCompare(a.id);
    });

  for (const file of directRows) {
    const mediaId = file.artifact_id as string;
    if (!assignments.has(mediaId)) {
      assignments.set(mediaId, {
        favoritedAt: file.favorited_at,
        favoritedBy: file.favorited_by,
        assignedAt: file.created_at,
      });
    }
  }

  return assignments;
}

/**
 * Order: favorites first; within BOTH groups, `assignedAt` desc (stable —
 * never reshuffles under a teammate's favorite clicks). Favorite toggles are
 * intentionally NOT re-sorted mid-interaction (see useToggleProjectMediaFavorite) —
 * this keeps the deferred reorder legible without teleporting a card the user
 * is currently pointing at.
 */
export function sortProjectMedia(items: ProjectMediaItem[]): ProjectMediaItem[] {
  return [...items].sort((a, b) => {
    const aFav = a.favoritedAt ? 1 : 0;
    const bFav = b.favoritedAt ? 1 : 0;
    if (aFav !== bFav) return bFav - aFav;
    return (b.assignedAt ?? '').localeCompare(a.assignedAt ?? '');
  });
}

const ASSIGNABLE_PROJECT_STATUSES = ['draft', 'active', 'generating', 'partial'] as const;

class ProjectAssignmentServiceClass extends EnhancedBaseService {
  async listProjectsByIds(projectIds: string[]): Promise<ServiceResponse<ProjectRow[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const ids = unique(projectIds);
      if (ids.length === 0) return [];

      const { data, error } = await this.client.from('projects').select('*').in('id', ids);

      if (error) throw new Error(error.message);
      return (data ?? []) as ProjectRow[];
    });
  }

  async listAssignableProjects(): Promise<ServiceResponse<ProjectRow[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('projects')
        .select('*')
        .in('status', [...ASSIGNABLE_PROJECT_STATUSES])
        .is('archived_at', null)
        .order('updated_at', { ascending: false })
        .limit(100);

      if (error) throw new Error(error.message);
      return (data ?? []) as ProjectRow[];
    });
  }

  async listMediaProjectAssignments(
    mediaIds: string[],
  ): Promise<ServiceResponse<MediaProjectAssignment[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const ids = unique(mediaIds);
      if (ids.length === 0) return [];

      const { data, error } = await this.client
        .from('project_files')
        .select('project_id, artifact_id')
        .eq('artifact_kind', 'media')
        .is('project_task_id', null)
        .in('artifact_id', ids);

      if (error) throw new Error(error.message);
      return (data ?? [])
        .filter((row) => row.artifact_id)
        .map((row) => ({
          mediaId: row.artifact_id as string,
          projectId: row.project_id,
        }));
    });
  }

  async addMediaToProjects(
    input: AddMediaToProjectsInput,
  ): Promise<ServiceResponse<AddMediaToProjectsResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const mediaIds = unique(input.mediaIds);
      const projectIds = unique(input.projectIds);
      if (mediaIds.length === 0 || projectIds.length === 0) {
        return { inserted: 0, projectNames: [] };
      }

      const { data: projects, error: projectsError } = await this.client
        .from('projects')
        .select('id, user_id, name')
        .in('id', projectIds);

      if (projectsError) throw new Error(projectsError.message);
      if (!projects || projects.length === 0) throw new Error('No active projects found');

      const projectById = new Map(projects.map((project) => [project.id, project]));
      const assignments = await this.listMediaProjectAssignments(mediaIds);
      if (assignments.error) throw new Error(assignments.error.message);

      const existing = new Set(
        (assignments.data ?? []).map((item) => `${item.projectId}:${item.mediaId}`),
      );

      const rows = projectIds.flatMap((projectId) => {
        const project = projectById.get(projectId);
        if (!project) return [];
        return mediaIds
          .filter((mediaId) => !existing.has(`${projectId}:${mediaId}`))
          .map((mediaId) => ({
            project_id: projectId,
            user_id: project.user_id,
            kind: 'artifact',
            artifact_kind: 'media',
            artifact_id: mediaId,
            project_task_id: null,
          }));
      });

      if (rows.length > 0) {
        const { error } = await this.client.from('project_files').insert(rows);
        if (error && error.code !== '23505') throw new Error(error.message);
      }

      return {
        inserted: rows.length,
        projectNames: projects.map((project) => project.name || 'Untitled project'),
      };
    });
  }

  async addArtifactToProject(input: {
    projectId: string;
    artifactId?: string | null;
    artifactKind?: string;
    chatSessionId?: string | null;
  }): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      let resolvedArtifactId = input.artifactId ?? null;
      if (resolvedArtifactId && /^https?:\/\//i.test(resolvedArtifactId)) {
        resolvedArtifactId = await this.resolveMediaIdFromUrl(resolvedArtifactId);
      }

      const { data: project, error: projectError } = await this.client
        .from('projects')
        .select('id, user_id')
        .eq('id', input.projectId)
        .maybeSingle();

      if (projectError) throw new Error(projectError.message);
      const ownerId = project?.user_id ?? (await resolveOwnerId(userId));

      // Social posts don't live in `project_files` — the project workbench
      // (and every other post surface) reads `social_posts.campaign_id`
      // directly, and nothing reads a project_files row with
      // artifact_kind 'social_post'. Writing one there was a false-promise
      // affordance: PostBody's "Add to project" showed a success toast +
      // "Open project" CTA for something the project page could never
      // actually show (beta-readiness-scorecard-2026-07-11 P0 #4 /
      // interaction-contracts-2026-07-11 §3). Stamp the real column instead
      // — this is the same write `postService.assignToCampaign` performs
      // for the equivalent /files "attach to plan" affordance.
      if (input.artifactKind === 'social_post' && resolvedArtifactId) {
        const { error: postError } = await this.client
          .from('social_posts')
          .update({ campaign_id: input.projectId })
          .eq('id', resolvedArtifactId);
        if (postError) throw new Error(`Add to project failed: ${postError.message}`);
      } else if (resolvedArtifactId) {
        const { data: existing, error: existingError } = await this.client
          .from('project_files')
          .select('id')
          .eq('project_id', input.projectId)
          .eq('artifact_kind', input.artifactKind ?? 'message')
          .eq('artifact_id', resolvedArtifactId)
          .is('project_task_id', null)
          .maybeSingle();

        if (existingError) throw new Error(existingError.message);
        if (!existing) {
          const { error: insertErr } = await this.client.from('project_files').insert({
            project_id: input.projectId,
            user_id: ownerId,
            kind: 'artifact',
            artifact_kind: input.artifactKind ?? 'message',
            artifact_id: resolvedArtifactId,
            project_task_id: null,
          });
          if (insertErr && insertErr.code !== '23505') {
            throw new Error(`Add to project failed: ${insertErr.message}`);
          }
        }
      }

      if (input.chatSessionId) {
        const [aiResult, chatResult] = await Promise.all([
          this.client
            .from('ai_sessions')
            .update({ project_id: input.projectId, updated_at: new Date().toISOString() })
            .eq('id', input.chatSessionId),
          this.client
            .from('chat_sessions')
            .update({ project_id: input.projectId })
            .eq('id', input.chatSessionId),
        ]);
        if (aiResult.error && chatResult.error) {
          throw new Error(`Link session failed: ${aiResult.error.message}`);
        }
      }
    });
  }

  async listProjectMedia(projectId: string): Promise<ServiceResponse<ProjectMediaItem[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: files, error: filesError } = await this.client
        .from('project_files')
        .select('id, artifact_id, project_task_id, created_at, favorited_at, favorited_by')
        .eq('project_id', projectId)
        .eq('artifact_kind', 'media')
        .is('project_task_id', null)
        .not('artifact_id', 'is', null)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });

      if (filesError) throw new Error(filesError.message);
      const assignmentById = aggregateDirectProjectMediaAssignments(
        (files ?? []) as ProjectMediaAssignmentRow[],
      );
      const mediaIds = [...assignmentById.keys()];
      if (mediaIds.length === 0) return [];

      const { data: media, error: mediaError } = await this.client
        .from('media')
        .select('*')
        .in('id', mediaIds);

      if (mediaError) throw new Error(mediaError.message);
      const mediaById = new Map((media ?? []).map((row) => [row.id, row as MediaRow]));
      const items = mediaIds
        .map((id): ProjectMediaItem | null => {
          const mediaRow = mediaById.get(id);
          if (!mediaRow) return null;
          const assignment = assignmentById.get(id);
          return {
            media: mediaRow,
            favoritedAt: assignment?.favoritedAt ?? null,
            favoritedBy: assignment?.favoritedBy ?? null,
            assignedAt: assignment?.assignedAt ?? null,
          };
        })
        .filter((item): item is ProjectMediaItem => item !== null);
      return sortProjectMedia(items);
    });
  }

  async setProjectMediaFavorite(input: {
    projectId: string;
    mediaId: string;
    favorited: boolean;
  }): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('project_files')
        .update({
          favorited_at: input.favorited ? new Date().toISOString() : null,
          favorited_by: input.favorited ? userId : null,
        })
        .eq('project_id', input.projectId)
        .eq('artifact_kind', 'media')
        .eq('artifact_id', input.mediaId)
        .is('project_task_id', null)
        .select('id');
      if (error) throw new Error(error.message);
      if (!data || data.length === 0) {
        throw new Error('Media is not assigned to this project');
      }
    });
  }

  private async resolveMediaIdFromUrl(url: string): Promise<string | null> {
    const { data, error } = await this.client
      .from('media')
      .select('id')
      .eq('url', url)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data?.id ?? null;
  }
}

function unique(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter(Boolean) as string[])];
}

export const projectAssignmentService = new ProjectAssignmentServiceClass();
